import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, BadgeCheck, Clock, Star } from "lucide-react";
import { CTAButton, Badge } from "@/components/landing/kit";
import { HERO } from "@/lib/site-data";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TRUST_ICONS = [ShieldCheck, BadgeCheck, Clock];

const Hero = () => (
  <section
    data-testid="hero-section"
    className="relative overflow-hidden border-b border-[#E5E8F0] pt-36 pb-20 sm:pt-40 sm:pb-28"
  >
    {/* ambient light + blue glow */}
    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(45,91,255,0.08),transparent_55%)]" />
    <div className="pointer-events-none absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#2D5BFF]/[0.07] blur-[140px]" />

    <div className="relative z-10 mx-auto max-w-5xl px-5 text-center sm:px-6">
      <motion.div variants={fadeUp} initial="hidden" animate="show" custom={0}>
        <Badge>{HERO.badge}</Badge>
      </motion.div>

      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={1}
        className="mx-auto mt-8 max-w-2xl font-heading text-xs font-bold uppercase tracking-[0.28em] text-[#6B7385] sm:text-sm"
      >
        {HERO.overline}
      </motion.p>

      <motion.h1
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={2}
        className="mx-auto mt-6 max-w-4xl text-balance font-heading text-5xl font-black leading-[0.98] tracking-tight text-[#0F1117] sm:text-7xl lg:text-[5rem]"
      >
        We build, stock &amp; run your{" "}
        <span className="text-[#2D5BFF]">Amazon, Walmart, eBay, Shopify &amp; Temu</span>{" "}
        store —{" "}
        <span className="accent-underline text-[#2D5BFF]">done for you.</span>
      </motion.h1>

      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={3}
        className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-[#4B5262] sm:text-lg"
      >
        {HERO.subhead}
      </motion.p>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={4}
        className="mt-10 flex flex-col items-center gap-4"
      >
        <CTAButton size="xl" testId="hero-cta-button">
          {HERO.cta}
        </CTAButton>
        <p className="text-xs text-[#858D9E] sm:text-sm">{HERO.microcopy}</p>
      </motion.div>

      {/* TrustCaptain rating badge */}
      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={5}
        data-testid="trust-rating-badge"
        className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-5"
      >
        <div className="flex items-center gap-3">
          <div className="flex gap-0.5">
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className="flex h-6 w-6 items-center justify-center rounded-[4px] bg-[#00B67A]"
              >
                <Star className="h-3.5 w-3.5 text-white" fill="currentColor" strokeWidth={0} />
              </span>
            ))}
          </div>
          <span className="text-sm text-[#363A45]">
            <span className="font-bold text-[#0F1117]">5-star rating</span> based on 143 reviews
          </span>
        </div>
        <span className="hidden h-4 w-px bg-[#E5E8F0] sm:block" />
        <span className="flex items-center gap-1.5">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#00B67A]">
            <Star className="h-3 w-3 text-white" fill="currentColor" strokeWidth={0} />
          </span>
          <span className="font-heading text-sm font-bold tracking-tight text-[#0F1117]">
            TrustCaptain
          </span>
        </span>
      </motion.div>

      <motion.div
        variants={fadeUp}
        initial="hidden"
        animate="show"
        custom={5}
        className="mx-auto mt-12 flex max-w-3xl flex-col items-center justify-center gap-x-8 gap-y-3 border-t border-[#E5E8F0] pt-8 sm:flex-row"
      >
        {HERO.trust.map((t, i) => {
          const Icon = TRUST_ICONS[i % TRUST_ICONS.length];
          return (
            <span
              key={t}
              className="flex items-center gap-2 font-heading text-[11px] font-bold uppercase tracking-[0.16em] text-[#6B7385] sm:text-xs"
            >
              <Icon className="h-4 w-4 text-[#2D5BFF]" />
              {t}
            </span>
          );
        })}
      </motion.div>
    </div>
  </section>
);

export default Hero;
